import { InvoiceListResponseType } from "@sellar-io/api-v2/types";
import { PaginationState } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { Icon, createSellarColumnHelper } from "@sellar-io/components";
import dayjs from "dayjs";
import { formatCurrency } from "@sellar-io/common";
import {
  BadgeGreen,
  BadgeGrey,
  BadgeRed,
  BadgeAmber,
} from "../../../../components/badge/Badge";
import { useInvoicePaginatedList } from "../../../../hooks/api/v2/invoice/useInvoiceList";

import { SpinnerLegacy } from "../../../../components";
import { useInvoicesFilterStore } from "../stores/InvoicesFilterStore";
import { useShallow } from "zustand/shallow";
import { TablePaginated } from "@sellar-io/components";

type ColumnDataType = InvoiceListResponseType[number];

export const InvoicesTable = ({
  setSelectedInvoice,
  setActive,
  search,
}: {
  setSelectedInvoice: (invoice: InvoiceListResponseType[number]) => void;
  setActive: (value: boolean) => void;
  search: string;
}) => {
  const [sorting, setSorting] = useState<{ id: string; desc: boolean }[]>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 25,
  });

  const state = useInvoicesFilterStore(useShallow((state) => state));

  const invoiceQuery = useInvoicePaginatedList({
    limit: pagination.pageSize,
    offset: pagination.pageIndex * pagination.pageSize,
    search,
    status: state.status && state.status !== "all" ? state.status : undefined,
    type: "openingOrderFees", // currently only show opening order fees
    sortBy: (sorting[0]?.id as "issuedAt" | "stripeInvoiceId") || undefined,
    sortDir: sorting[0]?.desc ? "desc" : "asc",
  });
  const { data } = invoiceQuery;
  const flatData = useMemo(() => data?.rows.map((page) => page) ?? [], [data]);
  const count = data?.count ?? 0;

  const pageCount = Math.ceil(count / pagination.pageSize);

  const handleTableSort = (columnSort: string | null) => {
    const [id, dir] = columnSort?.split("_") || [];
    const desc = dir === "desc";

    if (id && (sorting[0]?.id !== id || sorting[0].desc !== desc)) {
      setSorting([{ id, desc }]);
    }
  };

  const columnHelper = createSellarColumnHelper<ColumnDataType>();

  const columns = [
    columnHelper.accessor("type", {
      id: "type",
      header: "Fee",
      enableSorting: false,
      cell: (info) => {
        const type = info.getValue();
        switch (type) {
          case "openingOrderFees":
            return "Opening Order";
          case "sellarCreditRetailer":
            return "Sellar Credit";
          case "sellarCreditSupplier":
            return "Sellar Credit";
          default:
            return type;
        }
      },
    }),
    columnHelper.accessor("customId", {
      id: "stripeInvoiceId",
      header: "Invoice",
      enableSorting: true,
      cell: (info) => {
        return (
          info.getValue() ||
          info.row.original.stripeInvoiceId ||
          info.row.original.id
        );
      },
    }),
    columnHelper.accessor("issuedAt", {
      id: "issuedAt",
      header: "Issue date",
      enableSorting: true,
      cell: (info) =>
        info.getValue() ? dayjs(info.getValue()).format("DD/MM/YYYY") : "NA",
    }),
    columnHelper.accessor("total", {
      id: "total",
      header: "Amount",
      enableSorting: false,
      cell: (info) => formatCurrency(info.getValue()),
    }),
    columnHelper.accessor((row) => row, {
      id: "status",
      header: "Status",
      enableSorting: false,
      cell: (info) => {
        const row = info.getValue();
        switch (row.status) {
          case "draft":
            return <BadgeGrey className="text-sm font-medium">Draft</BadgeGrey>;
          case "issued":
            return (
              <BadgeAmber className="text-sm font-medium">
                Issued:&nbsp;{dayjs(row.issuedAt).format("DD/MM/YYYY")}
              </BadgeAmber>
            );
          case "paid":
            return (
              <BadgeGreen className="text-sm font-medium">
                Paid:&nbsp;{dayjs(row.paidAt).format("DD/MM/YYYY")}
              </BadgeGreen>
            );
          case "void":
            return <BadgeRed className="text-sm font-medium">Void</BadgeRed>;
          default:
            return row.status;
        }
      },
    }),
    columnHelper.accessor((row) => row, {
      id: "action",
      header: "",
      enableSorting: false,
      cell: () => (
        <div className="flex w-full justify-end">
          <Icon icon="CHEVRON_RIGHT" size={1} />
        </div>
      ),
    }),
  ];

  if (invoiceQuery.isLoading && flatData.length === 0) {
    return <SpinnerLegacy />;
  }

  return (
    <TablePaginated
      columns={columns}
      data={flatData}
      pageCount={pageCount}
      pagination={pagination}
      setPagination={setPagination}
      columnSortName={
        sorting.length
          ? `${sorting[0]?.id}_${sorting[0]?.desc ? "desc" : "asc"}`
          : undefined
      }
      handleTableSort={handleTableSort}
      onRowClick={(row) => {
        setSelectedInvoice(row);
        setActive(true);
      }}
      noResultsText="Your search returned 0 results"
      paginationLabel="Invoices per page"
      gridClasses="grid-cols-[1fr_minmax(250px,_1fr)_repeat(3,_1fr)_40px]"
    />
  );
};
